import * as React from "react";
import { useCallback, useContext, useSyncExternalStore } from "react";

import { ReactReduxContext, type ReactReduxContextValue } from "react-redux";

import classNames from "classnames";
import { type Map as ImmutableMap } from "immutable";

import { type RootState } from "flavours/glitch/store";

interface Props extends React.HTMLAttributes<HTMLImageElement> {
  id: string,
  className?: string,
  children?: never,
}

// Favourites are shown as hearts by default. The local setting
// `favourite_stars` switches back to the classic star icons.
const HEART_ICONS: Record<string, string> = {
  "star": "heart",
  "star-filled": "heart-filled",
};

const noopUnsubscribe = () => undefined;

// Reads the setting straight from the store, so that `Icon` also works in the
// few places that render without a Redux provider (it then shows hearts).
const useFavouriteStars = (): boolean => {
  const store = (useContext(ReactReduxContext) as ReactReduxContextValue | null)?.store;

  const subscribe = useCallback(
    (onChange: () => void) => store ? store.subscribe(onChange) : noopUnsubscribe,
    [store],
  );

  const getSnapshot = useCallback(
    () => {
      if (!store) {
        return false;
      }

      const settings = (store.getState() as RootState).local_settings as ImmutableMap<string, unknown>;
      return !!settings.get("favourite_stars");
    },
    [store],
  );

  return useSyncExternalStore(subscribe, getSnapshot);
};

export const Icon: React.FC<Props> = ({ id, className, title, ...other }) => {
  const favouriteStars = useFavouriteStars();
  const heartId = favouriteStars ? undefined : HEART_ICONS[id];

  return (
    <svg className={classNames("gts-icon", { "gts-icon-heart": heartId }, className)} {...other}>
      {title && <title>{title}</title>}
      <use href={`/icons.svg#${heartId ?? id}`} />
    </svg>
  );
};
